/* iceberg-ui */
import { BadgeVariant, Flex } from '@auviknetworks/iceberg-ui';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';

/*  Components  */
import {
    AppNameLinkCell,
    ApplicationsBubblesCell,
    BadgeCell,
    DateFormatCell,
    LinkTextCell,
    RecipientsCell,
    ReportActionsCell,
    ReportNameClickableCell,
    ReportTypeCell,
    RiskCell,
    SSOSupportIconsCell,
    StringCell,
    TenantNameCell,
    UserBubblesCell,
    UserCell
} from '../../Cells';
import {
    Header,
    ManagedTableV2ContextProvider,
    ManagedTableV2UI,
    TableHeadingProps
} from '../../Table';
import { useDashboardContext } from '../DashboardContext';
import { templateInterpolation } from '../Helpers/templateInterpolation';
import {
    type Column as ColumnType,
    type TablePanel as TablePanelType,
    Variables
} from '../types';
import { Panel } from './Panel';
import { usePanelContext } from './PanelContext';

/**
 * Cell type definitions for different column types in the table
 * @property {function} application - Renders application name with optional category and link
 * @property {function} applications - Renders multiple applications as bubbles
 * @property {function} date - Renders formatted date values
 * @property {function} dateNoTimeString - Renders formatted date values without time
 * @property {function} reportActions - Renders report action controls
 * @property {function} reportConfiguration - Renders report configuration details
 * @property {function} risk - Renders risk values
 * @property {function} ssoCoverage - Renders SSO support icons
 * @property {function} text - Renders plain text values
 * @property {function} user - Renders user information with icon and name
 * @property {function} userById - Renders user information by ID
 * @property {function} users - Renders multiple users as bubbles
 */
const getCells = ({
    variables,
    format
}: {
    variables?: Variables;
    format: TablePanelType['format'];
}): Record<ColumnType['type'], unknown> => ({
    application: (props: React.ComponentProps<typeof AppNameLinkCell>) => (
        <AppNameLinkCell {...props} withCategory={false} nameHasLink />
    ),
    applications: ApplicationsBubblesCell,
    badge: (props: React.ComponentProps<typeof BadgeCell>) => (
        <BadgeCell
            {...props}
            badgeVariants={
                format?.tagVariants as Record<string, BadgeVariant> | undefined
            }
        />
    ),
    date: DateFormatCell,
    dateNoTimeString: (props: React.ComponentProps<typeof DateFormatCell>) => (
        <DateFormatCell {...props} noTimeString />
    ),
    linkText: (props: React.ComponentProps<typeof LinkTextCell>) => (
        <LinkTextCell {...props} variables={variables} />
    ),
    recipients: RecipientsCell,
    reportActions: ReportActionsCell,
    reportNameClickable: ReportNameClickableCell,
    reportType: ReportTypeCell,
    risk: RiskCell,
    ssoCoverage: SSOSupportIconsCell,
    tenantName: TenantNameCell,
    text: (props: React.ComponentProps<typeof StringCell>) => (
        <StringCell {...props} variables={variables} />
    ),
    user: (props: React.ComponentProps<typeof UserCell>) => {
        const userData = props.cell.getValue();
        return <UserCell row={{ original: userData }} />;
    },
    userById: (props: React.ComponentProps<typeof UserCell>) => (
        <UserCell byId {...props} />
    ),
    users: UserBubblesCell
});

const TableContent = () => {
    const { t } = useTranslation();
    const { context: dashboardContext, schema: dashboard } =
        useDashboardContext();
    const {
        queryResults: { data, isFetching },
        schema: { format = {}, meta },
        context: panelContext,
        panelState
    } = usePanelContext<TablePanelType>();

    const {
        columns = [],
        enableFiltering = false,
        enablePagination = true,
        enableSorting = true,
        enableSearch = false,
        pageSize = 5,
        sorting
    } = format;

    const interpolationVariables = {
        ...panelContext?.variables,
        panelState: panelState as unknown as Variables
    };

    const rowsPerPage =
        typeof pageSize === 'string'
            ? parseInt(
                  templateInterpolation(
                      pageSize,
                      interpolationVariables
                  ) as string,
                  10
              )
            : pageSize;

    const renderFooter =
        typeof enablePagination === 'string'
            ? templateInterpolation(
                  enablePagination,
                  interpolationVariables
              ) === 'true'
            : enablePagination;

    const rows = Array.isArray(data) ? data : data?.data;

    const cells = getCells({
        format: dashboard.format,
        variables: dashboardContext?.variables
    });

    const tableColumns = columns.map((column) => ({
        accessorKey: column.accessor,
        cell:
            cells[
                templateInterpolation(
                    column.type,
                    interpolationVariables
                ) as keyof typeof cells
            ] || StringCell,
        enableColumnFilter: column.meta?.enableColumnFilter ?? true,
        enableSorting: column.meta?.enableSorting ?? true,
        ...(column.meta?.filterFn ? { filterFn: column.meta?.filterFn } : {}),
        ...(column.meta?.getUniqueValues
            ? {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  getUniqueValues: (row: any) =>
                      row[column.accessor]?.map((item: string) => item) || []
              }
            : {}),
        header: Header,
        id: column.id || column.accessor,
        labelKey: column.format?.labelKey,
        maxSize: column.maxSize || null,
        name: t(column.header || ''),
        size: column.size || null
    }));

    return (
        <ManagedTableV2ContextProvider
            columns={tableColumns}
            ignoreSearchParams
            mode="client"
            rows={rows}
            rowsPerPage={rowsPerPage}
            externalLoading={isFetching}
            enableColumnFilters={enableFiltering}
            enableSorting={enableSorting}
            searchEnabled={enableSearch}
            sortDirection={sorting?.desc ? 'DESC' : 'ASC'}
            sortKey={sorting?.key}
        >
            <Flex
                sx={{
                    flexDirection: 'column',
                    minHeight: '400px',
                    overflow: 'auto'
                }}
            >
                <ManagedTableV2UI
                    renderFooter={renderFooter}
                    {...(meta?.titleDef
                        ? ({ titleDef: meta?.titleDef } as TableHeadingProps)
                        : {})}
                />
            </Flex>
        </ManagedTableV2ContextProvider>
    );
};

/**
 * Renders a Table panel
 * @param panel - The panel configuration containing data and schema
 * @returns The rendered table panel
 */
export const TablePanel = ({ panel }: { panel: TablePanelType }) => (
    <Panel panel={panel}>
        <TableContent />
    </Panel>
);
